import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Save, AlertTriangle, ShieldCheck, Bell, Check } from "lucide-react";

const fmtUSD = n => "$" + Number(n || 0).toFixed(n < 1 ? 4 : 2);
const STATUS = {
  ok:       { cls: "pill-success", label: "OK" },
  alert:    { cls: "pill-warn",    label: "ALERT" },
  exceeded: { cls: "pill-error",   label: "EXCEEDED" },
  no_cap:   { cls: "pill",          label: "no cap" },
};

export default function QuotasPage() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState(null);
  const [emailsRaw, setEmailsRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    const [q, a] = await Promise.all([
      api.get("/admin/quotas"),
      api.get("/admin/quotas/alerts"),
    ]);
    setData(q.data);
    setForm(q.data.quota);
    setEmailsRaw((q.data.quota.notification_emails || []).join(", "));
    setAlerts(a.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = {
        ai_monthly_usd_cap: Number(form.ai_monthly_usd_cap),
        infra_monthly_usd_cap: Number(form.infra_monthly_usd_cap),
        ai_alert_threshold_pct: Number(form.ai_alert_threshold_pct),
        infra_alert_threshold_pct: Number(form.infra_alert_threshold_pct),
        throttle_when_exceeded: !!form.throttle_when_exceeded,
        notification_emails: emailsRaw.split(",").map(s => s.trim()).filter(Boolean),
      };
      await api.put("/admin/quotas", payload);
      setMsg("Quota saved. Alerts re-evaluated.");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const ack = async (id) => {
    await api.post(`/admin/quotas/alerts/${id}/ack`);
    await load();
  };

  if (!data || !form) return <div className="p-6 text-ink-soft">Loading…</div>;
  const u = data.usage;

  return (
    <div className="p-6 space-y-5" data-testid="quotas-root">
      <div>
        <div className="section-label">Admin · Cost Governance</div>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Cost Alerts & Quotas</h1>
        <p className="text-sm text-ink-soft">Set monthly USD ceilings for AI and Infra. When the alert threshold is crossed, a record is logged; when the hard cap is exceeded with throttle on, AI calls return HTTP 429.</p>
      </div>

      {msg && <div className="text-sm text-status-success border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2 flex items-center gap-2"><Check size={14}/> {msg}</div>}
      {err && <div className="text-sm text-status-error border border-red-200 bg-red-50 rounded-md px-3 py-2">{err}</div>}

      {/* Live usage tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { scope: "AI",    spend: u.ai_spend_usd,    cap: form.ai_monthly_usd_cap,    pct: u.ai_pct,    status: u.ai_status,    testid: "tile-ai" },
          { scope: "Infra", spend: u.infra_spend_usd, cap: form.infra_monthly_usd_cap, pct: u.infra_pct, status: u.infra_status, testid: "tile-infra" },
        ].map((s) => {
          const meta = STATUS[s.status] || STATUS.ok;
          const barColor = s.status === "exceeded" ? "bg-status-error" : s.status === "alert" ? "bg-status-warning" : "bg-brand";
          return (
            <div key={s.scope} className="card p-5" data-testid={s.testid}>
              <div className="flex items-center justify-between">
                <div className="section-label">{s.scope} · MTD {u.month}</div>
                <span className={"pill " + meta.cls}>{meta.label}</span>
              </div>
              <div className="flex items-end gap-3 mt-2">
                <div className="metric-value">{fmtUSD(s.spend)}</div>
                <div className="text-ink-soft text-sm pb-1">of {fmtUSD(s.cap)} cap · {s.pct}%</div>
              </div>
              <div className="mt-3 h-2 bg-surface rounded-full overflow-hidden">
                <div className={"h-full " + barColor} style={{width: `${Math.min(100, s.pct)}%`}}/>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quota form */}
      <div className="card p-5 space-y-4">
        <div className="section-label">Monthly caps & thresholds</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="dlabel">AI monthly cap (USD)</label>
            <input data-testid="q-ai-cap" type="number" step="0.01" value={form.ai_monthly_usd_cap}
              onChange={e=>setForm({...form, ai_monthly_usd_cap: e.target.value})}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="dlabel">AI alert threshold (%)</label>
            <input data-testid="q-ai-thresh" type="number" min="10" max="100" value={form.ai_alert_threshold_pct}
              onChange={e=>setForm({...form, ai_alert_threshold_pct: e.target.value})}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="dlabel">Infra monthly cap (USD)</label>
            <input data-testid="q-infra-cap" type="number" step="0.01" value={form.infra_monthly_usd_cap}
              onChange={e=>setForm({...form, infra_monthly_usd_cap: e.target.value})}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="dlabel">Infra alert threshold (%)</label>
            <input data-testid="q-infra-thresh" type="number" min="10" max="100" value={form.infra_alert_threshold_pct}
              onChange={e=>setForm({...form, infra_alert_threshold_pct: e.target.value})}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="dlabel">Notification recipients (comma-separated)</label>
            <input data-testid="q-emails" value={emailsRaw} onChange={e=>setEmailsRaw(e.target.value)}
              placeholder="finops@example.com, esg-lead@example.com"
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm" data-testid="q-throttle-label">
              <input data-testid="q-throttle" type="checkbox" checked={!!form.throttle_when_exceeded}
                onChange={e=>setForm({...form, throttle_when_exceeded: e.target.checked})}/>
              <span>Throttle AI calls when hard cap is exceeded (returns HTTP 429)</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end">
          <button data-testid="q-save" disabled={busy} onClick={save} className="btn btn-primary">
            <Save size={14}/> Save quota
          </button>
        </div>
      </div>

      {/* Alerts */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border section-label flex items-center gap-2">
          <Bell size={12}/> Alerts log
        </div>
        <table className="dense">
          <thead><tr><th>When</th><th>Scope</th><th>Level</th><th>Month</th><th className="text-right">Spend</th><th className="text-right">Cap</th><th className="text-right">%</th><th>Message</th><th></th></tr></thead>
          <tbody>
            {alerts.map(a => (
              <tr key={a.id} data-testid={`alert-${a.id}`}>
                <td className="font-mono text-xs">{a.ts?.slice(0,19).replace('T',' ')}</td>
                <td><span className="pill">{a.scope}</span></td>
                <td>
                  <span className={"pill " + (a.level === "exceeded" ? "pill-error" : "pill-warn")}>
                    {a.level === "exceeded" ? <AlertTriangle size={10} className="inline mr-1"/> : null}
                    {a.level}
                  </span>
                </td>
                <td className="font-mono text-xs">{a.month}</td>
                <td className="num">{fmtUSD(a.spend_usd)}</td>
                <td className="num">{fmtUSD(a.cap_usd)}</td>
                <td className="num">{a.pct}%</td>
                <td className="text-xs">{a.message}</td>
                <td>
                  {a.acknowledged ? (
                    <span className="pill pill-success"><ShieldCheck size={10} className="inline mr-1"/> acked</span>
                  ) : (
                    <button data-testid={`ack-${a.id}`} onClick={()=>ack(a.id)} className="btn btn-ghost">Ack</button>
                  )}
                </td>
              </tr>
            ))}
            {alerts.length === 0 && <tr><td colSpan={9} className="text-center text-ink-soft py-6">No cost alerts in this window.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
